import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmt, numToWords, toTitleCase, PAYMENT_TYPES } from './billing';

function getTextValue(value, preferredKeys = []) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of preferredKeys) {
      if (value[key] !== undefined && value[key] !== null && String(value[key]).trim()) {
        return String(value[key]).trim();
      }
    }
    const fallbackKey = Object.keys(value).find(k => typeof value[k] === 'string' && value[k].trim());
    return fallbackKey ? String(value[fallbackKey]).trim() : '';
  }
  return String(value).trim();
}

function getHotelNameText(value) {
  return getTextValue(value, ['displayName', 'name', 'hotelName', 'gstName']);
}

function getHotelAddressText(value) {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const parts = [
      getTextValue(value.address),
      getTextValue(value.city),
    ].filter(Boolean);
    return parts.join(', ');
  }
  return getTextValue(value, ['address', 'city']);
}

function getHotelGstText(value) {
  return getTextValue(value, ['gst', 'hotelGst', 'gstNumber', 'gst_number', 'gstName']);
}

function getHotelPhoneText(value) {
  return getTextValue(value, ['phone', 'mobile', 'contact']);
}

function formatDisplayName(salutation, name) {
  const parts = [salutation, name].map(s => (s || '').trim()).filter(Boolean);
  if (parts.length === 0) return 'Guest';
  return parts.map(toTitleCase).join(' ');
}

function formatPaymentTypeLabel(type) {
  const found = PAYMENT_TYPES.find(p => p.id === type);
  return found ? found.label : (type ? type.toUpperCase() : 'Cash');
}

export function buildInvoiceLineItems(reservation, billData, billingType) {
  const rows = [];
  const nights = Number(reservation.nights || 1);
  const rate = Number(reservation.rate || reservation.roomRate || 0);
  const room = reservation.room || reservation.roomNumber || 'Room';
  
  rows.push({
    description: `Room Stay Charges (${room})`,
    code: '996311',
    details: `${nights} night${nights > 1 ? 's' : ''} @ ${fmt(rate)}`,
    amount: rate * nights
  });

  // Items paid for on the spot (settlement:'paid') were already collected and
  // aren't part of the room bill — extra_charges on the server excludes them too,
  // so they're left off this itemized invoice entirely to avoid double-counting.
  const allSvcs = typeof reservation.extra_services === 'string'
    ? JSON.parse(reservation.extra_services || '[]')
    : (reservation.extra_services || []);
  const svcs = allSvcs.filter(s => s.settlement !== 'paid');

  let svcTotal = 0;
  if (svcs.length > 0) {
    svcs.forEach(s => {
      const amt = Number(s.amount || 0);
      svcTotal += amt;
      rows.push({
        description: s.name || 'Extra Service',
        code: '996331',
        details: s.quantity ? `${s.quantity} qty` : '-',
        amount: amt
      });
    });
  }

  const extraChargesVal = Number(reservation.extra_charges || reservation.extraCharges || 0);
  const remainingExtra = extraChargesVal - svcTotal;
  if (remainingExtra > 0) {
    rows.push({
      description: 'Service Charges / Extras',
      code: '996331',
      details: '-',
      amount: remainingExtra
    });
  }

  const damageVal = Number(reservation.damageCharges || reservation.damage_charges || 0);
  if (damageVal > 0) {
    rows.push({
      description: `Damage Charges${reservation.damageNote ? `: ${reservation.damageNote}` : ''}`,
      code: '-',
      details: '-',
      amount: damageVal,
      tone: 'danger'
    });
  }

  const discountVal = Number(reservation.discount || 0);
  if (discountVal > 0) {
    rows.push({
      description: 'Discount Applied',
      code: '-',
      details: '-',
      amount: -discountVal,
      tone: 'success'
    });
  }

  if (billingType === 'gst' && billData?.gstAmount > 0) {
    rows.push({
      description: `CGST (${(billData.gstRate || 5) / 2}%)`,
      code: '-',
      details: '-',
      amount: billData.gstAmount / 2
    });
    rows.push({
      description: `SGST (${(billData.gstRate || 5) / 2}%)`,
      code: '-',
      details: '-',
      amount: billData.gstAmount / 2
    });
  }

  return rows;
}

export async function generateInvoicePDF({
  reservation,
  guestData = {},
  hotelData = {},
  billDesign = {},
  paymentsList = [],
  invoiceNumber = null,
  billingType = null,
  invoiceItems = null,
  documentLabel = null
}) {
  try {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 12;
    let y = margin;

    const invNum = invoiceNumber || reservation.invoice_number || 'INV-001';
    const isGst = (billingType || reservation.billingType || 'non-gst') === 'gst';

    const hName = getHotelNameText(billDesign?.hotelName) || getHotelNameText(hotelData) || 'Hotel';
    const hAddr = getHotelAddressText(billDesign?.hotelAddress) || getHotelAddressText(hotelData) || 'NA';
    const hGst = getHotelGstText(billDesign?.hotelGst) || getHotelGstText(hotelData) || 'NA';
    const hPhone = getHotelPhoneText(hotelData) || 'NA';
    const hColor = hotelData.color || '#1f3a5f';
    const logoUrl = billDesign?.logoUrl || billDesign?.logo_url;

    const issueAt = new Date();
    const issueDate = issueAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const issueTime = issueAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

    const guestName = formatDisplayName(
      guestData.salutation || guestData.guestSalutation || reservation.guestSalutation,
      guestData.name || guestData.guestName || reservation.guestName
    );
    const guestPhone = guestData.phone || guestData.guestPhone || reservation.guestPhone || 'NA';
    const guestAddressRaw = getTextValue(guestData.address || reservation.address);
    const guestAddress = guestAddressRaw ? toTitleCase(guestAddressRaw) : 'NA';
    const companyName = getTextValue(reservation.company || guestData.company) || 'NA';
    const companyGst = getTextValue(reservation.companyGst || guestData.companyGst) || 'NA';

    const items = invoiceItems || buildInvoiceLineItems(reservation, null, isGst ? 'gst' : 'non-gst');
    const pdfTotal = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

    const settlementEntries = paymentsList
      .filter(entry => Number(entry?.amount) > 0)
      .map(entry => ({
        label: `${formatPaymentTypeLabel(entry.type)}${entry.note ? ` (${entry.note})` : ''}`,
        amount: Number(entry.amount) || 0
      }));
    const totalPaid = settlementEntries.reduce((sum, entry) => sum + entry.amount, 0);

    const hexToRgb = (hex) => {
      const normalized = /^#?[0-9A-Fa-f]{6}$/.test(String(hex || '').trim())
        ? String(hex).replace('#', '')
        : '1f3a5f';
      return [
        parseInt(normalized.slice(0, 2), 16),
        parseInt(normalized.slice(2, 4), 16),
        parseInt(normalized.slice(4, 6), 16)
      ];
    };
    const [accentR, accentG, accentB] = hexToRgb(hColor);
    const darkText = [33, 43, 57];
    const mutedText = [96, 106, 119];
    const softFill = [246, 248, 251];
    const borderColor = [216, 222, 230];
    const dangerText = [172, 57, 57];
    const successText = [33, 124, 81];

    const addContinuationHeader = () => {
      doc.setFillColor(accentR, accentG, accentB);
      doc.rect(0, 0, pageWidth, 5, 'F');
      y = 16;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11.5);
      doc.setTextColor(...darkText);
      doc.text(hName, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...mutedText);
      doc.text(`Invoice #${invNum}`, pageWidth - margin, y, { align: 'right' });
      y += 8;
      doc.setDrawColor(...borderColor);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
    };

    const ensureSpace = (needed) => {
      if (y + needed > pageHeight - 16) {
        doc.addPage();
        addContinuationHeader();
      }
    };

    doc.setFillColor(accentR, accentG, accentB);
    doc.rect(0, 0, pageWidth, 5, 'F');
    y = 14;

    const brandX = margin;
    let headerTextX = brandX;

    if (logoUrl) {
      try {
        doc.addImage(logoUrl, 'PNG', brandX, y, 22, 22);
        headerTextX = brandX + 26;
      } catch (_) {
        headerTextX = brandX;
      }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...darkText);
    doc.text(hName, headerTextX, y + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...mutedText);
    const addrLines = doc.splitTextToSize(hAddr, 100);
    doc.text(addrLines, headerTextX, y + 10);
    const afterAddrY = y + 10 + (addrLines.length * 3.8);

    doc.text(`Phone: ${hPhone}`, headerTextX, afterAddrY);
    if (isGst && hGst && hGst !== 'NA') {
      doc.text(`GSTIN: ${hGst}`, headerTextX, afterAddrY + 4.2);
    }

    const metaX = pageWidth - margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(accentR, accentG, accentB);
    const displayLabel = documentLabel || (isGst ? 'GST INVOICE' : 'INVOICE');
    doc.text(displayLabel, metaX, y + 5, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...mutedText);
    doc.text(`Invoice No: ${invNum}`, metaX, y + 10.5, { align: 'right' });
    doc.text(`Date: ${issueDate}`, metaX, y + 15, { align: 'right' });
    doc.text(`Time: ${issueTime}`, metaX, y + 19.5, { align: 'right' });

    y = Math.max(afterAddrY + 9, y + 24);
    doc.setDrawColor(...borderColor);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    const cardGap = 6;
    const cardWidth = (pageWidth - margin * 2 - cardGap) / 2;
    const cardHeight = 35;

    doc.setFillColor(...softFill);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, y, cardWidth, cardHeight, 2, 2, 'FD');
    doc.roundedRect(margin + cardWidth + cardGap, y, cardWidth, cardHeight, 2, 2, 'FD');

    let gy = y + 6;
    const guestX = margin + 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...darkText);
    doc.text('BILLED TO', guestX, gy);

    gy += 5.5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...darkText);
    doc.text(guestName, guestX, gy);

    gy += 4.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mutedText);
    if (guestPhone && guestPhone !== 'NA') {
      doc.text(`Phone: ${guestPhone}`, guestX, gy);
      gy += 4;
    }
    if (guestAddress && guestAddress !== 'NA') {
      const gAddrLines = doc.splitTextToSize(guestAddress, cardWidth - 8);
      doc.text(gAddrLines[0], guestX, gy);
      gy += 4;
    }
    if ((companyName && companyName !== 'NA') || (companyGst && companyGst !== 'NA')) {
      const compStr = companyName && companyName !== 'NA' ? companyName : '';
      const gstStr = companyGst && companyGst !== 'NA' ? `GSTIN: ${companyGst}` : '';
      const fullCompLabel = compStr && gstStr ? `Company: ${compStr} (${gstStr})` : (compStr ? `Company: ${compStr}` : gstStr);
      doc.text(fullCompLabel, guestX, gy);
    }

    let sy = y + 6;
    const stayX = margin + cardWidth + cardGap + 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...darkText);
    doc.text('STAY DETAILS', stayX, sy);

    sy += 5.5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
    const roomText = `Room ${reservation.room || reservation.roomNumber || '-'}`;
    doc.text(roomText, stayX, sy);

    sy += 4.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mutedText);
    const nightsText = `${reservation.nights || 1} Night${(reservation.nights || 1) > 1 ? 's' : ''}`;
    doc.text(`Check-In: ${reservation.checkIn || '-'}`, stayX, sy);
    sy += 4;
    doc.text(`Check-Out: ${reservation.checkOut || '-'} (${nightsText})`, stayX, sy);

    y += cardHeight + 8;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Description', 'SAC Code', 'Details', 'Amount']],
      body: items.map(item => [
        item.description,
        item.code || '-',
        item.details || '-',
        fmt(item.amount)
      ]),
      foot: [['GRAND TOTAL', '', '', fmt(pdfTotal)]],
      theme: 'grid',
      styles: {
        fontSize: 8.5,
        lineColor: borderColor,
        lineWidth: 0.2,
        textColor: darkText,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [34, 44, 60],
        textColor: 255,
        fontStyle: 'bold',
        cellPadding: 3.2
      },
      footStyles: {
        fillColor: [44, 58, 77],
        textColor: 255,
        fontSize: 9.5,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 88, fontStyle: 'bold' },
        1: { cellWidth: 24, halign: 'center' },
        2: { cellWidth: 28, halign: 'center' },
        3: { cellWidth: 38, halign: 'right', fontStyle: 'bold' }
      },
      alternateRowStyles: { fillColor: softFill },
      didParseCell: data => {
        const columnAlign = { 1: 'center', 2: 'center', 3: 'right' }[data.column.index];
        if (columnAlign) {
          data.cell.styles.halign = columnAlign;
        }
        if (data.section === 'body') {
          const row = items[data.row.index];
          if (row?.tone === 'danger') {
            data.cell.styles.textColor = dangerText;
          }
          if (row?.tone === 'success') {
            data.cell.styles.textColor = successText;
          }
        }
      }
    });

    y = doc.lastAutoTable.finalY + 7;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.3);
    doc.setTextColor(...mutedText);
    doc.text(`Amount in words: ${numToWords(pdfTotal)}`, margin, y);
    y += 7;

    ensureSpace(42);
    const settlementWidth = 102;
    const summaryWidth = pageWidth - margin * 2 - settlementWidth - 6;
    const settlementHeight = Math.max(26, 16 + (Math.max(settlementEntries.length, 1) * 5));
    const summaryHeight = 31;
    const blockHeight = Math.max(settlementHeight, summaryHeight);

    doc.setFillColor(...softFill);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, y, settlementWidth, blockHeight, 2, 2, 'FD');
    doc.roundedRect(margin + settlementWidth + 6, y, summaryWidth, blockHeight, 2, 2, 'FD');

    let setY = y + 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...darkText);
    doc.text('Settlement Details', margin + 4, setY);
    setY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mutedText);
    if (settlementEntries.length === 0) {
      doc.text('No payment entries recorded.', margin + 4, setY);
    } else {
      settlementEntries.forEach(entry => {
        doc.text(entry.label, margin + 4, setY);
        doc.text(fmt(entry.amount), margin + settlementWidth - 4, setY, { align: 'right' });
        setY += 5;
      });
    }

    setY = y + 6;
    const summaryX = margin + settlementWidth + 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...darkText);
    doc.text('Invoice Summary', summaryX, setY);
    setY += 6;

    const summaryRows = [
      ['Total Paid', fmt(totalPaid)],
    ];
    summaryRows.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.6);
      doc.setTextColor(...darkText);
      doc.text(label, summaryX, setY);
      doc.text(value, margin + settlementWidth + 6 + summaryWidth - 4, setY, { align: 'right' });
      setY += 5.3;
    });

    y += blockHeight + 8;

    const termsText = billDesign?.termsAndConditions || [
      "1. All guests must provide valid ID proof during check-in.",
      "2. Management is not responsible for any loss of valuable belongings.",
      "3. Any damage to hotel property will be charged to the guest.",
      "4. Final bill must be settled in full before check-out."
    ].join('\n');
    const termsLines = doc.splitTextToSize(termsText, pageWidth - margin * 2 - 8);
    const termsHeight = 12 + (termsLines.length * 4.2);
    ensureSpace(termsHeight + 28);

    doc.setFillColor(...softFill);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, y, pageWidth - margin * 2, termsHeight, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...darkText);
    doc.text('Terms & Conditions', margin + 4, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mutedText);
    doc.text(termsLines, margin + 4, y + 12);
    y += termsHeight + 12;

    const signatureBlockHeight = 22;
    ensureSpace(signatureBlockHeight + 10);
    const signatureLineY = y + 14;
    doc.setDrawColor(...borderColor);
    doc.line(margin, signatureLineY, margin + 55, signatureLineY);
    doc.line(pageWidth - margin - 55, signatureLineY, pageWidth - margin, signatureLineY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    doc.setTextColor(...mutedText);
    doc.text('Guest Signature', margin + 27.5, signatureLineY + 4.5, { align: 'center' });
    doc.text(billDesign?.signatureLabel || 'Authorized Signatory / Seal', pageWidth - margin - 27.5, signatureLineY + 4.5, { align: 'center' });
    y += signatureBlockHeight;

    const footerCopy = billDesign?.footerText || 'Thank you for your stay. We hope to welcome you again.';
    const pageCount = doc.getNumberOfPages();
    for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
      doc.setPage(pageIndex);
      doc.setDrawColor(...borderColor);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.2);
      doc.setTextColor(...mutedText);
      doc.text(footerCopy, pageWidth / 2, pageHeight - 7.2, { align: 'center' });
    }

    const pdfOutput = doc.output('blob');
    const pdfDataUrl = doc.output('datauristring');
    return { pdfOutput, pdfDataUrl, invNum };
  } catch (pdfErr) {
    console.error('PDF Generation failed:', pdfErr);
    throw new Error('PDF Generation failed: ' + pdfErr.message);
  }
}
